import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import QRCode from "qrcode";
import i18n from "@/lang";

/**
 * 购买完成后引导：个人中心 → 订单下载 → 许可证创建 → 扫码流程图
 *
 * 因为引导要跨 3 个页面（paySteps / PersonalCenter / LicenseApply），
 * 用 sessionStorage 携带阶段标记，每个页面 mounted 时检查标记衔接下一步：
 *   PHASE = 0  起步（paySteps 支付成功页）—— 高亮个人中心入口
 *   PHASE = 1  订单管理 tab —— 高亮「下载」按钮
 *   PHASE = 2  许可证申请 tab —— 高亮「创建许可证」，并展示扫码流程图占位 div
 */
const PHASE_KEY = "GUIDE_AFTERPAY_PHASE";
let activeDriver = null;
// 为 true 时，本次 destroy 不在 onDestroyed 里清理 PHASE 标记（用于跨页面推进阶段）
let keepPhaseOnDestroy = false;

/**
 * 销毁当前激活的引导（会触发 onDestroyed，从而按规则清理 PHASE 标记）
 * 用于用户绕过引导"下一步"、直接点击目标元素跳转的场景
 */
// 销毁当前引导但保留 PHASE 标记（内部使用，用于跨页面推进阶段）
function destroyActiveKeepPhase() {
  if (activeDriver) {
    keepPhaseOnDestroy = true;
    activeDriver.destroy();
    activeDriver = null;
    keepPhaseOnDestroy = false;
  }
}

export function destroyAfterPayGuide(options) {
  const keepPhase = !!(options && options.keepPhase);
  if (keepPhase) {
    destroyActiveKeepPhase();
  } else if (activeDriver) {
    activeDriver.destroy();
    activeDriver = null;
  }
}

export function clearAfterPayGuide() {
  sessionStorage.removeItem(PHASE_KEY);
}

export function setAfterPayPhase(phase) {
  sessionStorage.setItem(PHASE_KEY, String(phase));
}

export function getAfterPayPhase() {
  const v = sessionStorage.getItem(PHASE_KEY);
  return v == null ? null : Number(v);
}

const t = (key) => i18n.t(key);

/* eslint-disable */
function buildDriver(steps, onDestroyedCb) {
  const valid = steps.filter(
    (s) => s.element && document.querySelector(s.element)
  );
  if (valid.length === 0) {
    clearAfterPayGuide();
    return null;
  }
  const d = driver({
    showProgress: false,
    allowClose: true,
    overlayOpacity: 0.6,
    stagePadding: 6,
    stageRadius: 6,
    nextBtnText: t("guide.next"),
    prevBtnText: t("guide.prev"),
    doneBtnText: t("guide.done"),
    steps: valid,
    onDestroyed: () => {
      activeDriver = null;
      // 用户主动关闭（点 X / 点遮罩 / ESC）则中止整个引导；
      // 跨页面推进阶段（keepPhaseOnDestroy）时保留标记
      if (
        !keepPhaseOnDestroy &&
        getAfterPayPhase() !== null &&
        getAfterPayPhase() !== 99
      ) {
        clearAfterPayGuide();
      }
      onDestroyedCb && onDestroyedCb();
    },
  });
  return d;
}

/**
 * PHASE 0：支付成功页起步 —— 高亮「进入个人中心查看」按钮
 * 点「下一步」由调用方跳转到个人中心订单管理（设置 PHASE=1）
 */
export function startAfterPayGuidePersonal(onNext) {
  console.log("[引导] startAfterPayGuidePersonal 被调用");
  const steps = [
    {
      element: '[data-guide="goPersonalCenter"]',
      popover: {
        title: t("guide.stepPersonalTitle"),
        description: t("guide.stepPersonalDesc"),
        side: "top",
        align: "center",
        nextBtnText: t("guide.goNext"),
        onNextClick: () => {
          // 销毁当前步骤但保留并推进 PHASE 标记，引导在下一页面衔接
          destroyActiveKeepPhase();
          setAfterPayPhase(1);
          onNext && onNext();
        },
      },
    },
  ];
  console.log(
    "[引导] 目标元素存在?",
    !!document.querySelector('[data-guide="goPersonalCenter"]')
  );
  const d = buildDriver(steps);
  if (!d) {
    console.log("[引导] buildDriver 返回 null（元素找不到，引导被中止）");
    return;
  }
  console.log("[引导] driver 创建成功，开始引导");
  activeDriver = d;
  setAfterPayPhase(0);
  d.drive();
}

/**
 * PHASE 1：订单管理 tab —— 高亮「下载」按钮
 * 点「下一步」只设 PHASE=2，不再自动切 tab（PHASE 2 由 PersonalCenter 高亮许可证申请 tab）
 */
export function startAfterPayGuideOrder(onNext) {
  const steps = [
    {
      element: '[data-guide="orderDownload"]',
      popover: {
        title: t("guide.stepOrderTitle"),
        description: t("guide.stepOrderDesc"),
        side: "bottom",
        align: "center",
        nextBtnText: t("guide.goNext"),
        onNextClick: () => {
          // 销毁当前步骤但保留并推进 PHASE 标记，引导在下一页面衔接
          destroyActiveKeepPhase();
          setAfterPayPhase(2);
          onNext && onNext();
        },
      },
    },
  ];
  const d = buildDriver(steps);
  if (!d) {
    clearAfterPayGuide();
    return;
  }
  activeDriver = d;
  d.drive();
}

/**
 * PHASE 2：仍在订单管理 tab —— 高亮左侧「许可证申请」tab
 * 点「下一步」由调用方切到许可证申请（设置 PHASE=3）
 */
export function startAfterPayGuideLicenseTab(onNext) {
  const steps = [
    {
      element: '[data-guide="licenseTab"]',
      popover: {
        title: t("guide.stepLicenseTabTitle"),
        description: t("guide.stepLicenseTabDesc"),
        side: "right",
        align: "center",
        nextBtnText: t("guide.goNext"),
        onNextClick: () => {
          // 销毁当前步骤但保留并推进 PHASE 标记，引导在下一页面衔接
          destroyActiveKeepPhase();
          setAfterPayPhase(3);
          onNext && onNext();
        },
      },
    },
  ];
  const d = buildDriver(steps);
  if (!d) {
    clearAfterPayGuide();
    return;
  }
  activeDriver = d;
  d.drive();
}

/**
 * PHASE 3：许可证申请 tab —— 高亮「创建许可证」
 * 点「下一步」由调用方打开机器码弹框（设置 PHASE=4）
 * 弹窗内附带手机端说明 + 当前网址二维码，扫码可在手机端同步完成操作
 */
export function startAfterPayGuideLicense(onNext) {
  const start = (qrImg) => {
    const mobileTip =
      `<div style="margin-top:10px;padding-top:10px;border-top:1px solid #ebeef5;">` +
      `<div style="margin-bottom:8px;line-height:1.5;">${t("guide.stepLicenseMobile")}</div>` +
      (qrImg
        ? `<img src="${qrImg}" alt="mobile-qrcode" style="display:block;width:120px;height:120px;margin:0 auto;">`
        : "") +
      `</div>`;
    const steps = [
      {
        element: '[data-guide="createLicense"]',
        popover: {
          title: t("guide.stepLicenseTitle"),
          description: t("guide.stepLicenseDesc") + mobileTip,
          side: "top",
          align: "center",
          nextBtnText: t("guide.goNext"),
          onNextClick: () => {
            // 销毁当前步骤但保留并推进 PHASE 标记，引导在下一页面衔接
            destroyActiveKeepPhase();
            setAfterPayPhase(4);
            onNext && onNext();
          },
        },
      },
    ];
    const d = buildDriver(steps);
    if (!d) {
      clearAfterPayGuide();
      return;
    }
    activeDriver = d;
    d.drive();
  };
  // 生成当前网址二维码（手机扫码后由路由自动跳转到对应手机端页面）
  QRCode.toDataURL(window.location.href, { width: 240, margin: 1 })
    .then((url) => start(url))
    .catch(() => start(""));
}

/**
 * PHASE 4：机器码弹框 —— 高亮机器码输入框，引导结束
 */
export function startAfterPayGuideMachineCode() {
  const steps = [
    {
      element: '[data-guide="machineInput"]',
      popover: {
        title: t("guide.stepMachineTitle"),
        description: t("guide.stepMachineDesc"),
        side: "left",
        align: "center",
      },
    },
  ];
  const d = buildDriver(steps, () => {
    // 完成或关闭，引导全部结束
    setAfterPayPhase(99);
    setTimeout(() => clearAfterPayGuide(), 500);
  });
  if (!d) {
    clearAfterPayGuide();
    return;
  }
  activeDriver = d;
  d.drive();
}
